import type { OrgRegistrationBadge } from './cardModel'
import type { OrgRegistrationListRow } from './listRows'
import { getRegistrationSportName } from './sportArt'

/** The list view's sortable columns. */
export type OrgRegistrationListSortKey =
  | 'name'
  | 'sport'
  | 'location'
  | 'status'
  | 'window'
  | 'season'
  | 'countdown'

export type OrgRegistrationSortValue = string | number | null

/** Soonest to act on first — the order the status tabs read in. */
export const ORG_REGISTRATION_STATUS_SORT_RANK: Record<
  OrgRegistrationBadge,
  number
> = {
  closingSoon: 0,
  open: 1,
  upcoming: 2,
  past: 3,
}

const instant = (iso: string | null): number | null => {
  const time = iso === null ? Number.NaN : Date.parse(iso)
  return Number.isNaN(time) ? null : time
}

/**
 * What a column sorts a row by. Dates sort as instants — no zone moves an
 * order. Closes In sorts by the moment its countdown counts to: the close for
 * an open or closed registration, the open for an upcoming one. Location
 * sorts by the first venue's name. A detail row
 * has no values of its own; the grid re-seats it under its parent after a sort.
 */
export const getOrgRegistrationSortValue = (
  row: OrgRegistrationListRow | undefined,
  key: OrgRegistrationListSortKey
): OrgRegistrationSortValue => {
  if (!row || row.kind === 'detail') {
    return null
  }
  const { registration, model } = row
  switch (key) {
    case 'name':
      return registration.name || null
    case 'sport':
      return getRegistrationSportName(registration) || null
    case 'location':
      return registration.venues[0]?.name ?? null
    case 'status':
      return ORG_REGISTRATION_STATUS_SORT_RANK[model.badge]
    case 'window':
      return instant(registration.opensAt)
    case 'season':
      return instant(registration.seasonStartsAt)
    case 'countdown':
      return instant(
        model.badge === 'upcoming'
          ? registration.opensAt
          : registration.closesAt
      )
  }
}

/**
 * Compares two sort values for a grid that flips the result itself when the
 * sort is descending. A missing value goes last in BOTH directions, so a sort
 * never leads with blanks. Text compares case-insensitively, with numbers in
 * it read as numbers ('U9' before 'U10'). Ties return 0 — the grid's sort is
 * stable, so they keep the API's order.
 */
export const compareOrgRegistrationSortValues = (
  a: OrgRegistrationSortValue,
  b: OrgRegistrationSortValue,
  isDescending: boolean
): number => {
  if (a === null || b === null) {
    if (a === b) {
      return 0
    }
    const missingLast = a === null ? 1 : -1
    return isDescending ? -missingLast : missingLast
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
  }
  return Number(a) - Number(b)
}
